import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { PaymentProviderRow, ProviderType } from '../../types';

const HELP: Record<ProviderType, string> = {
  stripe: 'Hosted Stripe Checkout. Requires a Stripe account.',
  manual: 'No automated checkout. You upgrade users out-of-band after receiving payment.',
  none: 'Disable upgrades entirely.',
};

export function PaymentsPage() {
  const [rows, setRows] = useState<PaymentProviderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Record<string, string>>({});

  async function reload() {
    try { setRows(await api('/api/admin/payments')); } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }
  useEffect(() => { void reload(); }, []);

  async function activate(type: ProviderType) {
    try { await api(`/api/admin/payments/${type}/activate`, { method: 'POST' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }

  async function save(type: ProviderType) {
    const text = edit[type] ?? '{}';
    let parsed: unknown;
    try { parsed = JSON.parse(text); } catch { alert('Config must be valid JSON.'); return; }
    if (typeof parsed !== 'object' || parsed === null) { alert('Config must be a JSON object.'); return; }
    try {
      await api(`/api/admin/payments/${type}`, { method: 'PUT', body: JSON.stringify({ config: parsed }) });
      setEdit((s) => { const { [type]: _, ...rest } = s; return rest; });
      await reload();
    } catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>Payments</h1>
      </header>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>
        Pick the active payment provider. Only one can be active at a time. Secrets are stored in the database (plaintext) — protect via DB access.
      </p>
      {error && <div className="form-error">{error}</div>}
      {!rows && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {rows && (
        <div className="provider-list">
          {rows.map((r) => {
            const value = edit[r.type] ?? JSON.stringify(r.config, null, 2);
            return (
              <div key={r.type} className={`provider-card${r.isActive ? ' is-active' : ''}`}>
                <div className="provider-head">
                  <div>
                    <h3>{r.type}</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>{HELP[r.type]}</p>
                  </div>
                  {r.isActive ? (
                    <span className="badge badge-active">Active</span>
                  ) : (
                    <button type="button" className="btn-secondary" onClick={() => void activate(r.type)}>Activate</button>
                  )}
                </div>
                <div className="form-group">
                  <label>Config (JSON)</label>
                  <textarea
                    className="admin-textarea"
                    rows={5}
                    value={value}
                    onChange={(e) => setEdit((s) => ({ ...s, [r.type]: e.target.value }))}
                  />
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn-primary" onClick={() => void save(r.type)}>Save config</button>
                  {r.type === 'stripe' && !r.usable && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Needs <code>secretKey</code> and <code>webhookSecret</code> to be usable.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
